import styled, { keyframes } from "styled-components";

const breakpoints = {
  xl: "80rem",
  lg: "64rem",
  md: "48rem",
  sm: "31.25rem",
};

const float = keyframes`
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-0.75rem);
  }
  100% {
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    opacity: 0.25;
  }
  100% {
    opacity: 0.7;
  }
`;

export const StyledHome = styled.section`
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  width: 100%;
  padding: clamp(4rem, 8vw, 6rem) clamp(1.5rem, 5vw, 6rem)
    clamp(3rem, 6vw, 5rem);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: clamp(2rem, 6vw, 6rem);
  align-items: center;
  background: radial-gradient(
      circle at 10% 20%,
      rgba(255, 99, 233, 0.25),
      transparent 55%
    ),
    radial-gradient(
      circle at 80% 10%,
      rgba(92, 154, 255, 0.2),
      transparent 50%
    ),
    ${({ theme }) => theme.backgroundColor.dark};
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
        rgba(255, 255, 255, 0.04) 1px,
        transparent 1px
      ),
      linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
    background-size: 140px 140px;
    opacity: 0.4;
    z-index: 0;
    mask-image: radial-gradient(circle, rgba(0, 0, 0, 0.6), transparent 65%);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(41, 41, 50, 0.95),
      rgba(15, 15, 20, 0.85)
    );
    opacity: 0.35;
    z-index: -1;
  }

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
    padding: 4rem 1.5rem 3rem;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const HeroEyebrow = styled.span`
  letter-spacing: 0.4em;
  text-transform: uppercase;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.color.lightGray};
  opacity: 0.85;
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
  width: fit-content;
  padding: 0.4rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
`;

export const Name = styled.h2`
  font-size: clamp(2.5rem, 6vw, 3.75rem);
  color: ${({ theme }) => theme.color.light};
  margin: 0;
  font-weight: 700;
`;

export const HeroTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.15;
  color: ${({ theme }) => theme.color.light};
  max-width: 34rem;
  margin: 0;
`;

export const HeroSubtitle = styled.p`
  color: ${({ theme }) => theme.color.lightGray};
  max-width: 36rem;
  line-height: 1.7;
  font-size: 1.125rem;
  margin: 0;
`;

export const Accent = styled.span`
  color: ${({ theme }) => theme.backgroundColor.orange};
  font-weight: 600;
`;

export const MetricsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 0.5rem;
`;

export const Metric = styled.div`
  min-width: 8rem;
`;

export const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.color.light};
  letter-spacing: -0.04em;
`;

export const MetricLabel = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.color.lightGray};
`;

export const StyledButtonsContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const HeroBadge = styled.div`
  padding: 0.75rem 1.25rem;
  border-radius: 999px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.color.light};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

export const SocialTray = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  color: ${({ theme }) => theme.color.lightGray};
  font-size: 0.8rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;

  span {
    font-size: 0.75rem;
    opacity: 0.75;
  }
`;

export const HeroVisual = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

export const VisualCard = styled.div`
  position: relative;
  width: min(32rem, 100%);
  padding: clamp(2rem, 4vw, 2.75rem);
  border-radius: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(15, 16, 25, 0.75);
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(22px);
  overflow: hidden;
`;

export const VisualGlow = styled.div`
  position: absolute;
  width: 18rem;
  height: 18rem;
  background: radial-gradient(
    circle,
    rgba(255, 164, 94, 0.35),
    rgba(255, 164, 94, 0)
  );
  filter: blur(30px);
  z-index: 0;
  inset: auto;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0.7;
  animation: ${pulse} 5s ease-in-out infinite alternate;
`;

export const FloatingBadge = styled.div`
  position: absolute;
  top: 1.5rem;
  right: clamp(0rem, 4vw, -1rem);
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  backdrop-filter: blur(12px);
  animation: ${float} 6s ease-in-out infinite;
  transform: translateY(0);
  text-align: left;
  color: ${({ theme }) => theme.color.light};

  p {
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin: 0;
    opacity: 0.7;
  }

  strong {
    display: block;
    font-size: 1rem;
    margin-top: 0.35rem;
  }

  span {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.color.lightGray};
  }
`;

export const HeroHighlights = styled.div`
  display: grid;
  width: 100%;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
`;

export const HighlightCard = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(12px);
  min-height: 7rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const HighlightLabel = styled.span`
  font-size: 0.65rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.backgroundColor.orange};
`;

export const HighlightTitle = styled.h3`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.color.light};
  margin: 0;
`;

export const HighlightMeta = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.color.lightGray};
`;

